'use client';

import { useSyncExternalStore } from 'react';

/**
 * Options for configuring the external store
 */
export type ExternalStoreOptions<T> = {
  /**
   * Optional callback that runs when state is updated
   * @param state Current state after update
   */
  onStateChange?: (state: T) => void;

  /**
   * Optional callback that runs when state is accessed
   * @param state Current state being accessed
   */
  onStateAccess?: (state: T) => void;
};

/**
 * External store API returned by makeExternalStore
 */
export type ExternalStore<T> = {
  /**
   * Get the current state
   */
  getState: () => T;

  /**
   * Subscribe to state changes
   * @param listener Function to call when state changes
   * @returns Unsubscribe function
   */
  subscribe: (listener: () => void) => () => void;

  /**
   * Set the state partially or completely
   * @param nextState Partial or complete state update
   */
  setState: (nextState: Partial<T>) => void;
};

/**
 * Hook API returned by makeExternalStore
 */
export type ExternalStoreHook<T> = {
  /**
   * React hook to use the external store
   * @returns Current state and setState function
   */
  useStore: () => T & { setState: (nextState: Partial<T>) => void };
};

/**
 * Direct API for non-React code
 */
export type ExternalStoreAPI<T> = {
  /**
   * Get the current state
   */
  getState: () => T;

  /**
   * Set the state partially or completely
   * @param nextState Partial or complete state update
   */
  setState: (nextState: Partial<T>) => void;
};

/**
 * Complete return type of makeExternalStore
 */
export type ExternalStoreReturn<T> = {
  /**
   * The store instance
   */
  store: ExternalStore<T>;

  /**
   * React hook to use the store
   */
  useStore: () => T & { setState: (nextState: Partial<T>) => void };

  /**
   * Direct API for non-React code
   */
  api: ExternalStoreAPI<T>;
};

/**
 * Creates an external store that can be used with React's useSyncExternalStore
 * as well as from non-React code
 *
 * @param initialState Initial state of the store
 * @param options Configuration options
 * @returns Store, hook, and direct API
 *
 * @example
 * // Create a store
 * const { useStore, api } = makeExternalStore({ count: 0 });
 *
 * // Use in React components
 * function Counter() {
 *   const { count, setState } = useStore();
 *   return (
 *     <button onClick={() => setState({ count: count + 1 })}>
 *       Count: {count}
 *     </button>
 *   );
 * }
 *
 * // Use in non-React code
 * function incrementCount() {
 *   const { count } = api.getState();
 *   api.setState({ count: count + 1 });
 * }
 */
export function makeExternalStore<T extends Record<string, unknown>>(
  initialState: T,
  options?: ExternalStoreOptions<T>,
): ExternalStoreReturn<T> {
  let state = { ...initialState };
  const listeners = new Set<() => void>();

  const store: ExternalStore<T> = {
    getState: () => {
      options?.onStateAccess?.(state);
      return state;
    },

    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    setState: (nextState: Partial<T>) => {
      state = { ...state, ...nextState };
      options?.onStateChange?.(state);
      listeners.forEach((listener) => listener());
    },
  };

  const useStore = () => {
    const currentState = useSyncExternalStore(store.subscribe, store.getState, () => initialState);

    return {
      ...currentState,
      setState: store.setState,
    };
  };

  const api: ExternalStoreAPI<T> = {
    getState: store.getState,
    setState: store.setState,
  };

  return {
    store,
    useStore,
    api,
  };
}
